import React from "react";
import {
    Box,
    Typography,
    Tooltip,
    Stack,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
} from "@mui/material";
import {
    LocationOn as LocationIcon,
    PeopleAlt as PeopleAltIcon,
    ArrowOutward as ArrowOutwardIcon,
    BusinessCenter as BusinessCenterIcon,
    LockOutlined as LockIcon,
    Quiz as QuizIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

import { useTooltipProps } from "./JobCard";

// Custom minimal Spinner component for button loading states
export const Spinner = ({ color = "#fff", size = 12 }) => (
    <Box
        sx={{
            width: size,
            height: size,
            border: `2px solid ${alpha(color, 0.25)}`,
            borderTop: `2px solid ${color}`,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
            },
        }}
    />
);

// Dynamic status chip color config matching your design
export const useStatusStyle = (theme) => {
    return (status) => {
        const s = status?.toLowerCase();
        if (s === "open" || s === "active") return { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, label: "Active" };
        if (s === "onhold") return { bg: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, label: "On Hold" };
        return { bg: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, label: "Closed" };
    };
};

// Base design layout configuration for your row action buttons
export const useActionButtonStyles = (theme) => {
    return {
        applyBtnSx: { color: theme.palette.success.main, "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.08) } },
        matchBtnSx: { color: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) } },
        questionsBtnSx: { color: theme.palette.secondary.main, "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.08) } },
        skillsBtnSx: { color: theme.palette.warning.main, "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.08) } },
    };
};

const JobTable = ({
    data = [],
    viewingJdId,
    openingSkillsId,
    onView,
    onApply,
    onMatch,
    onSkills,
    onQuestions,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const ttProps = useTooltipProps(theme);
    const { applyBtnSx, matchBtnSx, skillsBtnSx, questionsBtnSx } = useActionButtonStyles(theme);
    const getStatusStyle = useStatusStyle(theme);

    // Fixed height + width for every action button — prevents any reflow
    const actionBtnBase = {
        flex: "none",
        height: 26,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        px: 0,
        fontSize: "0.65rem",
        lineHeight: 1,
    };

    const iconBtnSx = {
        p: 0,
        width: 26,
        height: 26,
        minWidth: 26,
        flexShrink: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "7px",
        bgcolor: theme.palette.background.paper,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
            bgcolor: alpha(theme.palette.text.primary, 0.05),
        },
    };

    return (
        <TableContainer
            component={Paper}
            sx={{
                borderRadius: "16px",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark
                    ? "0 8px 24px -10px rgba(0,0,0,0.4)"
                    : "0 4px 16px -8px rgba(15,23,42,0.06)",
                bgcolor: theme.palette.background.paper,
                overflow: "hidden",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                // Horizontal scroll on the container if viewport is too narrow
                overflowX: "auto",
            }}
        >
            <Table
                size="small"
                sx={{
                    // Prevents table from collapsing columns — keeps layout stable
                    tableLayout: "fixed",
                    minWidth: 780,
                }}
            >
                <colgroup>
                    <col style={{ width: "22%" }} />  {/* Job Title */}
                    <col style={{ width: "14%" }} />  {/* Location */}
                    <col style={{ width: "9%" }}  />  {/* Status */}
                    <col style={{ width: "11%" }} />  {/* Created */}
                    <col style={{ width: "9%" }}  />  {/* Applicants */}
                    <col style={{ width: "35%" }} />  {/* Actions */}
                </colgroup>

                {/* ── HEAD ── */}
                <TableHead>
                    <TableRow
                        sx={{
                            bgcolor: alpha(theme.palette.text.primary, 0.03),
                            "& .MuiTableCell-head": {
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                color: theme.palette.text.secondary,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                py: 1.4,
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            },
                        }}
                    >
                        <TableCell>Job Title</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="center">Applicants</TableCell>
                        <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
                    </TableRow>
                </TableHead>

                {/* ── BODY ── */}
                <TableBody>
                    {data.map((item, idx) => {
                        const statusStyle = getStatusStyle(item.status);
                        const hasApplicants = (item.applications || 0) > 0;
                        const isViewingThis = viewingJdId === item.id;
                        const isOpeningSkills = openingSkillsId === item.id;

                        const status = item?.status?.toLowerCase();
                        const isApplyDisabled = ["onhold", "closed"].includes(status);
                        const isSkillsDisabled = status === "closed";

                        return (
                            <TableRow
                                key={item.id}
                                sx={{
                                    bgcolor:
                                        idx % 2 === 0
                                            ? "transparent"
                                            : alpha(theme.palette.text.primary, 0.015),
                                    transition: "background 0.15s",
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                    },
                                    "& .MuiTableCell-root": {
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                        py: 1,
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        // Prevent any cell from wrapping and pushing height
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                    },
                                }}
                            >
                                {/* Title */}
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                flexShrink: 0,
                                                borderRadius: "6px",
                                                bgcolor: alpha(theme.palette.text.primary, 0.05),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <BusinessCenterIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                                        </Box>
                                        <Tooltip title={item.job_title} placement="top" {...ttProps}>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                    color: "text.primary",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    cursor: "default",
                                                    minWidth: 0,
                                                }}
                                            >
                                                {item.job_title}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </TableCell>

                                {/* Location */}
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
                                        <LocationIcon sx={{ fontSize: 11, color: "text.secondary", flexShrink: 0 }} />
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "text.secondary",
                                                fontWeight: 500,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.location || "—"}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                {/* Status */}
                                <TableCell>
                                    <Chip
                                        label={item.status || "open"}
                                        size="small"
                                        sx={{
                                            bgcolor: statusStyle.bg,
                                            color: statusStyle.color,
                                            border: `1px solid ${statusStyle.border}`,
                                            fontWeight: 700,
                                            fontSize: "0.58rem",
                                            height: 20,
                                            borderRadius: "5px",
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                            "& .MuiChip-label": { px: 0.8 },
                                        }}
                                    />
                                </TableCell>

                                {/* Created */}
                                <TableCell>
                                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", fontWeight: 500 }}>
                                        {item.created_at
                                            ? new Date(item.created_at).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                              })
                                            : "N/A"}
                                    </Typography>
                                </TableCell>

                                {/* Applicants */}
                                <TableCell align="center">
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                        <PeopleAltIcon
                                            sx={{
                                                fontSize: 11,
                                                color: hasApplicants ? "primary.main" : "text.secondary",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "0.72rem",
                                                fontWeight: 700,
                                                color: hasApplicants ? "primary.main" : "text.secondary",
                                            }}
                                        >
                                            {item.applications || 0}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                {/* Actions — fixed-width Stack so row height never shifts */}
                                <TableCell align="right">
                                    <Stack
                                        direction="row"
                                        spacing={0.6}
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        sx={{ flexWrap: "nowrap" }}
                                    >
                                        {/* View JD */}
                                        <Tooltip title="Open Job Description" placement="top" {...ttProps}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onView && onView(item)}
                                                    disabled={!!viewingJdId}
                                                    sx={iconBtnSx}
                                                >
                                                    {isViewingThis ? (
                                                        <Spinner color={theme.palette.primary.main} size={11} />
                                                    ) : (
                                                        <ArrowOutwardIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        {/* Apply */}
                                        <Tooltip title="Upload a resume for this role" placement="top" {...ttProps} disableHoverListener={!isApplyDisabled}>
                                            <span>
                                                <Button
                                                    disabled={isApplyDisabled}
                                                    onClick={() => onApply && onApply(item)}
                                                    sx={{ ...applyBtnSx, ...actionBtnBase }}
                                                >
                                                    {isApplyDisabled ? (
                                                        <LockIcon sx={{ fontSize: "11px" }} />
                                                    ) : (
                                                        "Apply"
                                                    )}
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        {/* Match */}
                                        <Tooltip
                                            title={hasApplicants ? "Run AI matching" : "No applicants yet"}
                                            placement="top"
                                            {...ttProps}
                                        >
                                            <span>
                                                <Button
                                                    disabled={!hasApplicants}
                                                    onClick={() => onMatch && onMatch(item)}
                                                    sx={{ ...matchBtnSx, ...actionBtnBase }}
                                                >
                                                    {!hasApplicants
                                                        ? <LockIcon sx={{ fontSize: "11px" }} />
                                                        : "Match"
                                                    }
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        {/* Quiz / Questions */}
                                        <Tooltip title="Generate interview questions" placement="top" {...ttProps} disableHoverListener={!isSkillsDisabled}>
                                            <span>
                                                <Button
                                                    disabled={isSkillsDisabled}
                                                    onClick={() => onQuestions && onQuestions(item)}
                                                    sx={{ ...questionsBtnSx, ...actionBtnBase }}
                                                >
                                                    <QuizIcon sx={{ fontSize: "11px" }} />
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        {/* Skills */}
                                        <Tooltip
                                            title={isSkillsDisabled ? "Skills cannot be modified for a closed JD" : hasApplicants ? "Locked after applications received" : "Edit required skills"}
                                            placement="top"
                                            {...ttProps}
                                            disableHoverListener={!isSkillsDisabled && !hasApplicants}
                                        >
                                            <span>
                                                <Button
                                                    disabled={isSkillsDisabled || item.applications > 0 || isOpeningSkills}
                                                    onClick={() => onSkills && onSkills(item)}
                                                    sx={{ ...skillsBtnSx, ...actionBtnBase }}
                                                >
                                                    {isOpeningSkills ? (
                                                        <Spinner color={theme.palette.warning.main} size={11} />
                                                    ) : isSkillsDisabled || item.applications > 0 ? (
                                                        <LockIcon sx={{ fontSize: "11px" }} />
                                                    ) : (
                                                        "Skills"
                                                    )}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default JobTable;